/* eslint-disable no-console */
import { createStore, get, set, del, keys, clear } from 'idb-keyval';

const STORE_DB_NAME = 'simply-say-photo-library';
const STORE_NAME = 'photo-data';

let store;
const memoryFallback = new Map();
let hasLoggedWarning = false;
let persistentStoreIndicator = null;

const getStore = () => {
  if (typeof window === 'undefined') {
    persistentStoreIndicator = false;
    return null;
  }
  if (typeof window.indexedDB === 'undefined') {
    if (!hasLoggedWarning) {
      hasLoggedWarning = true;
      console.warn('IndexedDB not available; photo cache will not persist across reloads.');
    }
    persistentStoreIndicator = false;
    return null;
  }
  if (!store) {
    try {
      store = createStore(STORE_DB_NAME, STORE_NAME);
      persistentStoreIndicator = true;
    } catch (error) {
      if (!hasLoggedWarning) {
        hasLoggedWarning = true;
        console.warn('Unable to initialise IndexedDB photo cache.', error);
      }
      store = null;
      persistentStoreIndicator = false;
      return null;
    }
  }
  if (persistentStoreIndicator == null) {
    persistentStoreIndicator = Boolean(store);
  }
  return store;
};

const fallbackSet = (key, value) => {
  memoryFallback.set(key, value);
};

const fallbackGet = key => memoryFallback.get(key);

const fallbackDelete = key => {
  memoryFallback.delete(key);
};

const fallbackKeys = () => Array.from(memoryFallback.keys());

const fallbackClear = () => {
  memoryFallback.clear();
};

const photoLibraryCache = {
  async set(key, value) {
    if (!key) return;
    const dbStore = getStore();
    if (dbStore) {
      try {
        await set(key, value, dbStore);
        return;
      } catch (error) {
        if (!hasLoggedWarning) {
          hasLoggedWarning = true;
          console.warn('IndexedDB photo cache set failed; using memory fallback.', error);
        }
      }
    }
    fallbackSet(key, value);
  },

  async get(key) {
    if (!key) return null;
    const dbStore = getStore();
    if (dbStore) {
      try {
        const value = await get(key, dbStore);
        if (typeof value === 'undefined' && memoryFallback.has(key)) {
          return memoryFallback.get(key);
        }
        return value ?? null;
      } catch (error) {
        if (!hasLoggedWarning) {
          hasLoggedWarning = true;
          console.warn('IndexedDB photo cache get failed; using memory fallback.', error);
        }
      }
    }
    return fallbackGet(key) || null;
  },

  async delete(key) {
    if (!key) return;
    const dbStore = getStore();
    if (dbStore) {
      try {
        await del(key, dbStore);
        return;
      } catch (error) {
        if (!hasLoggedWarning) {
          hasLoggedWarning = true;
          console.warn('IndexedDB photo cache delete failed; using memory fallback.', error);
        }
      }
    }
    fallbackDelete(key);
  },

  async bulkSet(entries = []) {
    if (!Array.isArray(entries) || !entries.length) {
      return;
    }
    const dbStore = getStore();
    if (dbStore) {
      const stored = [];
      const failed = [];
      for (const entry of entries) {
        if (!entry || !entry.key) continue;
        try {
          await set(entry.key, entry.value, dbStore);
          stored.push(entry.key);
        } catch (error) {
          failed.push(entry.key);
          fallbackSet(entry.key, entry.value);
          if (!hasLoggedWarning) {
            hasLoggedWarning = true;
            console.warn('IndexedDB photo cache write failed; using memory fallback for some entries.', error);
          }
          if (error && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_FILE_NO_DEVICE_SPACE')) {
            persistentStoreIndicator = false;
          }
        }
      }
      return { stored, failed };
    }
    const failed = [];
    entries.forEach(({ key, value }) => {
      if (!key) return;
      failed.push(key);
      fallbackSet(key, value);
    });
    persistentStoreIndicator = false;
    return { stored: [], failed };
  },

  async deleteMany(keysToDelete = []) {
    if (!Array.isArray(keysToDelete) || !keysToDelete.length) {
      return;
    }
    const dbStore = getStore();
    if (dbStore) {
      try {
        await Promise.all(keysToDelete.map(key => del(key, dbStore)));
      } catch (error) {
        if (!hasLoggedWarning) {
          hasLoggedWarning = true;
          console.warn('IndexedDB photo cache deleteMany failed; falling back to memory.', error);
        }
        keysToDelete.forEach(fallbackDelete);
      }
      return;
    }
    keysToDelete.forEach(fallbackDelete);
  },

  async keys() {
    const dbStore = getStore();
    if (dbStore) {
      try {
        const value = await keys(dbStore);
        if (Array.isArray(value)) {
          return value;
        }
      } catch (error) {
        if (!hasLoggedWarning) {
          hasLoggedWarning = true;
          console.warn('IndexedDB photo cache keys failed; using memory fallback.', error);
        }
      }
    }
    return fallbackKeys();
  },

  async clear() {
    const dbStore = getStore();
    if (dbStore) {
      try {
        await clear(dbStore);
      } catch (error) {
        if (!hasLoggedWarning) {
          hasLoggedWarning = true;
          console.warn('IndexedDB photo cache clear failed; using memory fallback.', error);
        }
      }
    }
    fallbackClear();
  },

  async removeAllExcept(keysToKeep = []) {
    const keepSet = new Set((keysToKeep || []).filter(Boolean));
    const allKeys = await this.keys();
    const toDelete = allKeys.filter(key => !keepSet.has(key));
    await this.deleteMany(toDelete);
  },

  isPersistent() {
    if (persistentStoreIndicator == null) {
      getStore();
    }
    return persistentStoreIndicator === true;
  },
};

export { photoLibraryCache };
